import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';

import { SocketActionsFacade } from '@ps/core-data-access';
import { SurveyStatus } from '@ps/survey-kernel';
import {
  ScheduleLaunch,
  SurveyChannelCreationConfirmedEvent,
} from '@ps/survey-utils';
import { SURVEY_SOCKET_EVENTS } from '@ps/survey-data-access';

import {
  ManageSurveyActions,
  ManageSurveySiblingActions,
} from './manage-survey.actions';
import { MentionableUsersActions } from '../mentionable-users/mentionable-users.actions';

@Injectable({ providedIn: 'root' })
export class ManageSurveyActionsFacade {
  private readonly _store = inject(Store);
  private readonly _socketActionsFacade = inject(SocketActionsFacade);

  public loadSurveyDetails(surveyId: number): void {
    this._store.dispatch(ManageSurveyActions.loadSurveyDetails({ surveyId }));
  }

  public loadFullSurveyDetails(surveyId: number): void {
    this._store.dispatch(
      ManageSurveyActions.loadFullSurveyDetails({ surveyId }),
    );
  }

  public loadChildSurveyDetails(surveyId: number): void {
    this._store.dispatch(
      ManageSurveyActions.loadChildSurveyDetails({ surveyId }),
    );
  }

  public resetSurveyDetails(): void {
    this._store.dispatch(ManageSurveyActions.resetSurveyDetails());
  }

  public loadSurveyHealth(surveyId: number): void {
    this._store.dispatch(ManageSurveyActions.loadSurveyHealth({ surveyId }));
  }

  public loadRemainingHours(surveyId: number): void {
    this._store.dispatch(ManageSurveyActions.loadRemainingHours({ surveyId }));
  }

  public loadReconciliationAttempts(surveyId: number): void {
    this._store.dispatch(
      ManageSurveyActions.loadReconciliationAttempts({ surveyId }),
    );
  }

  public loadActivityLogs(surveyId: number): void {
    this._store.dispatch(ManageSurveyActions.loadActivityLogs({ surveyId }));
  }

  public resetActivityLogs(): void {
    this._store.dispatch(ManageSurveyActions.resetActivityLogs());
  }

  public loadComments(surveyId: number): void {
    this._store.dispatch(ManageSurveyActions.loadComments({ surveyId }));
  }

  public addComment(surveyId: number, body: string): void {
    this._store.dispatch(ManageSurveyActions.addComment({ surveyId, body }));
  }

  public saveCrmId(surveyId: number, crmId: string): void {
    this._store.dispatch(ManageSurveyActions.saveCrmId({ surveyId, crmId }));
  }

  public saveAsanaId(surveyId: number, asanaId: string): void {
    this._store.dispatch(
      ManageSurveyActions.saveAsanaId({ surveyId, asanaId }),
    );
  }

  public saveBillingNumber(surveyId: number, billingNumber: string): void {
    this._store.dispatch(
      ManageSurveyActions.saveBillingNumber({ surveyId, billingNumber }),
    );
  }

  public saveCustomRedirectField(surveyId: number, value: string): void {
    this._store.dispatch(
      ManageSurveyActions.saveCustomRedirectField({ surveyId, value }),
    );
  }

  public updateCurrentCpi(
    surveyId: number,
    cpi: number,
    isChild = false,
  ): void {
    this._store.dispatch(
      ManageSurveySiblingActions.updateCurrentCpi({ surveyId, cpi, isChild }),
    );
  }

  public updateCompletesRequired(
    surveyId: number,
    completesRequired: number,
    isChild = false,
  ): void {
    this._store.dispatch(
      ManageSurveySiblingActions.updateCompletesRequired({
        surveyId,
        completesRequired,
        isChild,
      }),
    );
  }

  public updateScheduleLaunch(
    surveyId: number,
    scheduleLaunch: ScheduleLaunch,
    isChild = false,
  ): void {
    this._store.dispatch(
      ManageSurveySiblingActions.updateScheduleLaunch({
        surveyId,
        scheduleLaunch,
        isChild,
      }),
    );
  }

  public loadSurveyQuotas(surveyId: number, page = 1, search?: string): void {
    this._store.dispatch(
      ManageSurveyActions.loadSurveyQuotas({ surveyId, page, search }),
    );
  }

  public loadMoreQuotas(surveyId: number): void {
    this._store.dispatch(ManageSurveyActions.loadMoreQuotas({ surveyId }));
  }

  public loadAllQuotas(surveyId: number): void {
    this._store.dispatch(ManageSurveyActions.loadAllQuotas({ surveyId }));
  }

  public resetSurveyQuotas(): void {
    this._store.dispatch(ManageSurveyActions.resetSurveyQuotas());
  }

  public setQuotaLock(
    surveyId: number,
    psQuotaId: string,
    locked: boolean,
  ): void {
    this._store.dispatch(
      ManageSurveyActions.setQuotaLock({ surveyId, psQuotaId, locked }),
    );
  }

  public setQuotaCurrentTarget(
    surveyId: number,
    psQuotaId: string,
    currentTarget: number,
    updateSupplier: boolean,
  ): void {
    this._store.dispatch(
      ManageSurveyActions.setQuotaCurrentTarget({
        surveyId,
        psQuotaId,
        currentTarget,
        updateSupplier,
      }),
    );
  }

  public setQuotaCurrentlyOpen(
    surveyId: number,
    psQuotaId: string,
    currentlyOpen: number,
  ): void {
    this._store.dispatch(
      ManageSurveyActions.setQuotaCurrentlyOpen({
        surveyId,
        psQuotaId,
        currentlyOpen,
      }),
    );
  }

  public setSupplierQuotaCurrentTarget(
    surveyId: number,
    psQuotaId: string,
    supplierRefId: string,
    currentTarget: number,
  ): void {
    this._store.dispatch(
      ManageSurveyActions.setSupplierQuotaCurrentTarget({
        surveyId,
        psQuotaId,
        supplierRefId,
        currentTarget,
      }),
    );
  }

  public setSupplierQuotaCurrentlyOpen(
    surveyId: number,
    psQuotaId: string,
    supplierRefId: string,
    currentlyOpen: number,
  ): void {
    this._store.dispatch(
      ManageSurveyActions.setSupplierQuotaCurrentlyOpen({
        surveyId,
        psQuotaId,
        supplierRefId,
        currentlyOpen,
      }),
    );
  }

  public renameQuota(surveyId: number, psQuotaId: string, label: string): void {
    this._store.dispatch(
      ManageSurveyActions.renameQuota({ surveyId, psQuotaId, label }),
    );
  }

  public setQuotaCpi(surveyId: number, psQuotaId: string, cpi: number): void {
    this._store.dispatch(
      ManageSurveyActions.setQuotaCpi({ surveyId, psQuotaId, cpi }),
    );
  }

  public loadTrafficChannels(surveyId: number): void {
    this._store.dispatch(ManageSurveyActions.loadTrafficChannels({ surveyId }));
  }

  public resetTrafficChannels(): void {
    this._store.dispatch(ManageSurveyActions.resetTrafficChannels());
  }

  public setTrafficChannelStatus(surveyId: number, status: SurveyStatus): void {
    this._store.dispatch(
      ManageSurveyActions.setTrafficChannelStatus({ surveyId, status }),
    );
  }

  public updateTrafficChannelCpi(surveyId: number, cpi: number): void {
    this._store.dispatch(
      ManageSurveyActions.updateTrafficChannelCpi({ surveyId, cpi }),
    );
  }

  public launchTrafficChannel(
    surveyId: number,
    event: SurveyChannelCreationConfirmedEvent,
  ): void {
    this._store.dispatch(
      ManageSurveyActions.launchTrafficChannel({ surveyId, event }),
    );
  }

  public downloadRespondentListTemplate(): void {
    this._store.dispatch(ManageSurveyActions.downloadRespondentListTemplate());
  }

  public uploadPsidFile(surveyId: number, file: File): void {
    this._store.dispatch(
      ManageSurveyActions.uploadPsidFile({ surveyId, file }),
    );
  }

  public removePsidFile(filePath: string): void {
    this._store.dispatch(ManageSurveyActions.removePsidFile({ filePath }));
  }

  public discardPsidFile(filePath: string): void {
    this._store.dispatch(ManageSurveyActions.discardPsidFile({ filePath }));
  }

  public clearPsidFile(): void {
    this._store.dispatch(ManageSurveyActions.clearPsidFile());
  }

  public loadMentionableUsers(): void {
    this._store.dispatch(MentionableUsersActions.loadMentionableUsers());
  }

  public joinSurveyRoom(surveyId: number): void {
    this._socketActionsFacade.joinRoom(
      SURVEY_SOCKET_EVENTS.JOIN_SURVEY_ROOM,
      surveyId,
    );
  }

  public leaveSurveyRoom(surveyId: number): void {
    this._socketActionsFacade.leaveRoom(
      SURVEY_SOCKET_EVENTS.LEAVE_SURVEY_ROOM,
      surveyId,
    );
  }

  public joinQuotaTab(surveyId: number): void {
    this._socketActionsFacade.joinRoom(
      SURVEY_SOCKET_EVENTS.JOIN_QUOTA_TAB_ROOM,
      surveyId,
    );
  }

  public leaveQuotaTab(surveyId: number): void {
    this._socketActionsFacade.leaveRoom(
      SURVEY_SOCKET_EVENTS.LEAVE_QUOTA_TAB_ROOM,
      surveyId,
    );
  }

  public joinSupplierTab(surveyId: number): void {
    this._socketActionsFacade.joinRoom(
      SURVEY_SOCKET_EVENTS.JOIN_SUPPLIER_TAB_ROOM,
      surveyId,
    );
  }

  public leaveSupplierTab(surveyId: number): void {
    this._socketActionsFacade.leaveRoom(
      SURVEY_SOCKET_EVENTS.LEAVE_SUPPLIER_TAB_ROOM,
      surveyId,
    );
  }

  public joinTCTab(surveyId: number): void {
    this._socketActionsFacade.joinRoom(
      SURVEY_SOCKET_EVENTS.JOIN_TC_TAB_ROOM,
      surveyId,
    );
  }

  public leaveTCTab(surveyId: number): void {
    this._socketActionsFacade.leaveRoom(
      SURVEY_SOCKET_EVENTS.LEAVE_TC_TAB_ROOM,
      surveyId,
    );
  }

  public subscribeTrafficSurveys(surveyIds: number[]): void {
    this._socketActionsFacade.joinRoom(
      SURVEY_SOCKET_EVENTS.SUBSCRIBE_TO_SURVEYS,
      {
        surveyIds,
      },
    );
  }

  public unsubscribeTrafficSurveys(surveyIds: number[]): void {
    this._socketActionsFacade.leaveRoom(
      SURVEY_SOCKET_EVENTS.UNSUBSCRIBE_FROM_SURVEYS,
      { surveyIds },
    );
  }
}
